import type { UsageStatsResponse } from "@/lib/types";

import { DailyBarChart } from "./DailyBarChart";
import { formatInt, formatTokens, getModelLabel } from "./formatters";
import { HorizontalBarRow } from "./HorizontalBarRow";
import { SourceStackedBar } from "./SourceStackedBar";

export function StatsOverview({ data }: { data: UsageStatsResponse }) {
  const maxModelTokens =
    data.byModel.length === 0 ? 0 : Math.max(...data.byModel.map((r) => r.totalTokens));

  const maxUserTokens =
    data.byUser.length === 0 ? 0 : Math.max(...data.byUser.map((r) => r.totalTokens));

  const dailyData = data.byDay.map((row) => ({
    day: row.day,
    responses: row.responses,
    value: row.totalTokens,
  }));

  return (
    <>
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-lg border border-cyber-border bg-cyber-surface/30 px-3 py-2">
          <div className="text-[10px] uppercase tracking-wide text-cyber-muted/70">Prompts</div>
          <div className="mt-1 text-sm text-cyber-text">{formatInt(data.totals.responses)}</div>
        </div>
        <div className="rounded-lg border border-cyber-border bg-cyber-surface/30 px-3 py-2">
          <div className="text-[10px] uppercase tracking-wide text-cyber-muted/70">
            Total tokens
          </div>
          <div className="mt-1 text-sm text-cyber-cyan">
            {formatTokens(data.totals.totalTokens)}
          </div>
        </div>
        <div className="rounded-lg border border-cyber-border bg-cyber-surface/30 px-3 py-2">
          <div className="text-[10px] uppercase tracking-wide text-cyber-muted/70">
            Avg steps / prompt
          </div>
          <div className="mt-1 text-sm text-cyber-text">{data.totals.avgStepCount}</div>
        </div>
      </div>

      {/* Daily total tokens chart */}
      <section className="rounded-lg border border-cyber-border bg-cyber-surface/20 p-3">
        <h3 className="mb-3 text-xs uppercase tracking-wide text-cyber-muted/80">
          Daily total tokens
        </h3>
        <DailyBarChart data={dailyData} valueLabel="tokens" />
      </section>

      {/* Two-column grid: Model + Source/Person */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {/* By Model */}
        <section className="rounded-lg border border-cyber-border bg-cyber-surface/20 p-3">
          <h3 className="mb-3 text-xs uppercase tracking-wide text-cyber-muted/80">By model</h3>
          <div className="max-h-80 space-y-3 overflow-y-auto pr-1">
            {data.byModel.map((row) => (
              <HorizontalBarRow
                key={`${row.providerId}/${row.modelId}`}
                label={getModelLabel(row.providerId, row.modelId, row.modelName)}
                value={row.totalTokens}
                maxValue={maxModelTokens}
                formattedValue={formatTokens(row.totalTokens)}
                subtitle={`${formatInt(row.responses)} responses`}
              />
            ))}
          </div>
        </section>

        {/* Right column: Source + Person */}
        <div className="space-y-3">
          {/* By Source */}
          <section className="rounded-lg border border-cyber-border bg-cyber-surface/20 p-3">
            <h3 className="mb-3 text-xs uppercase tracking-wide text-cyber-muted/80">By source</h3>
            <SourceStackedBar data={data.bySource} />
          </section>

          {/* By Person */}
          <section className="rounded-lg border border-cyber-border bg-cyber-surface/20 p-3">
            <h3 className="mb-3 text-xs uppercase tracking-wide text-cyber-muted/80">By person</h3>
            <div className="max-h-64 space-y-3 overflow-y-auto pr-1">
              {data.byUser.map((row) => (
                <HorizontalBarRow
                  key={row.userEmail}
                  label={row.userEmail}
                  value={row.totalTokens}
                  maxValue={maxUserTokens}
                  formattedValue={formatTokens(row.totalTokens)}
                  subtitle={`${formatInt(row.responses)} responses`}
                  color="rgb(var(--cyber-magenta))"
                />
              ))}
            </div>
          </section>
        </div>
      </div>
    </>
  );
}
